import React, { useEffect, useReducer, useCallback } from "react";
import { Dialog, DialogTitle, DialogContent, Button, Box, Divider, Tooltip, IconButton } from '@mui/material'
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add"
import userPermissions from "../User/Permissions";
import {
    DataGridPro,
    ptBR,
    GridToolbarContainer
} from '@mui/x-data-grid-pro';
import { getSession } from "../../auth";
import { ptBR as corePtBR } from '@mui/material/locale';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import './styles.css'
import { lockedReducer } from "../../utils/defaultReducer";
import api from "../../api";
import useNotification from "../Notification";
import EditWebForms from "./edit";
import Loading from '../Loading'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CircleIcon from '@mui/icons-material/Circle';
import ConfirmationDialogCaptcha from "../Dialogs/ConfimationDialogCaptcha";
import dayjs from 'dayjs'
import ConfirmationDialog from "../Dialogs/Confirmation";

const theme = createTheme({
    palette: {
        primary: { main: '#21446C' },
    },
}, ptBR, corePtBR);

export const buildColumns = (state, setState) => {
    const columns = []
    columns.push({ field: 'name', headerName: 'Nome', flex: 1 })
    columns.push({ field: 'submission_count', headerName: 'Submissões', flex: 1 })
    columns.push({ field: 'created_at', headerName: 'Criação', width: 150, renderCell: ({ value }) => dayjs.utc(value).local().format('DD/MM/YYYY HH:mm') })
    columns.push({
        field: 'actions', headerName: 'Ações', width: 150, renderCell: ({ row }) => (
            <>
                <Tooltip title={row.active ? 'Ativo': 'Inativo'}>
                    <IconButton onClick={() => setState({ active: row })}>
                        <CircleIcon sx={{ color: row.active === true ? 'green !important' : 'red !important' }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Editar" >
                    <IconButton
                        className="action-button"
                        onClick={() => { setState({ edit: { ...row, step: 2 } }) }}
                    >
                        <EditIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Copiar" >
                    <IconButton
                        className="action-button"
                        onClick={() => { setState({ copy: row }) }}
                    >
                        <ContentCopyIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title='Excluir Formulário'>
                    <IconButton
                        className='action-button delete'
                        onClick={() => setState({ delete: row })}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </>
        )
    })
    return columns
}


const WebForms = ({ handleClose, journey }) => {

    const userGroups = userPermissions()
    const enterpriseId = getSession()?.profile?.enterprise.id

    const [state, setState] = useReducer(lockedReducer, {
        rows: [],
        loading: false,
        edit: null,
        delete: null,
        copy: null,
        active: null,
        contactCustomFields: {},
        contactEnterpriseCustomFields: {},
        journeyCustomFields: {},
    })

    const initialize = async () => {
        setState({ loading: true })

        const contactCustomFields = await api.get(`api/enterprises/${enterpriseId}/custom_fields/`)
        if (contactCustomFields.status === 200)
            setState({ contactCustomFields: contactCustomFields.data })
        const contactEnterpriseCustomFields = await api.get(`api/enterprises/${enterpriseId}/enterprise_custom_fields/`)
        if (contactEnterpriseCustomFields.status === 200)
            setState({ contactEnterpriseCustomFields: contactEnterpriseCustomFields.data })
        const journeyCustomFields = await api.get(`api/enterprises/${enterpriseId}/journeys/${journey.id}/custom_fields/`)
        if (journeyCustomFields.status === 200)
            setState({ journeyCustomFields: journeyCustomFields.data })

        const res = await api.get(`api/enterprises/${enterpriseId}/forms/?journey=${journey?.id}`)
        if (res.status === 200) {
            setState({ rows: res.data, loading: false, delete: null, copy: null, active: null })
        } else {
            setState({ loading: false })
            useNotification('Ops!', 'Houve um erro para carregar os formulários', 'danger')
        }
    }

    useEffect(() => {
        initialize()
    }, [])

    const CustomToolbar = useCallback(() => (
        <GridToolbarContainer sx={{ display: "flex", gap: 1 }}>
            <Button size="small" onClick={() => setState({ edit: {} })} startIcon={<AddIcon />}>
                Adicionar
            </Button>
        </GridToolbarContainer>
    ), [])

    const remove = async () => {

        setState({ loading: true })
        const res = await api.delete(`api/enterprises/${enterpriseId}/forms/${state.delete.id}/`)
        setState({ loading: false })
        if (res.status === 204) {
            useNotification(
                'Sucesso!',
                'Formulário removido com sucesso!',
                'success'
            )
            await initialize()
        } else {
            useNotification(
                'Ops!',
                'Ocorreu um erro para remover o formulário',
                'danger'
            )
        }

    }

    const copy = async () => {
        setState({ loading: true })
        const res = await api.post(`api/enterprises/${enterpriseId}/forms/${state.copy.id}/copy/`)
        setState({ loading: false })
        if (res.status === 200) {
            useNotification(
                'Sucesso!',
                'Formulário copiado com sucesso!',
                'success'
            )
            await initialize()
        } else {
            useNotification(
                'Ops!',
                'Ocorreu um erro para copiar o formulário',
                'danger'
            )
        }
    }

    const toggle = async () => {
        setState({ loading: true })
        const res = await api.post(`api/enterprises/${enterpriseId}/forms/${state.active.id}/toggle/`, {active: !state.active.active})
        setState({ loading: false })
        if (res.status === 200) {
            useNotification(
                'Sucesso!',
                `Formulário ${state.active.active ? 'desativado' : 'ativado'}} com sucesso!`,
                'success'
            )
            await initialize()
        } else {
            useNotification(
                'Ops!',
                'Ocorreu um erro para atualizar o formulário',
                'danger'
            )
        }
    }

    return (
        <>
            <Loading loading={state.loading} />
            {state.copy && <ConfirmationDialog
                title='Confirmação'
                description={<>Deseja copiar o formulário <span style={{ fontWeight: 'bold' }}>{state.copy.name}</span>?
                </>}
                open={true}
                handleClose={() => setState({ copy: null })}
                handleSubmit={copy}
            />}
            {state.active && <ConfirmationDialogCaptcha
                captcha={state.active.active ? 'desativar' : 'ativar'}
                title={`${state.active.active ? 'Desativar' : 'Ativar'} formulário`}
                description={<>Deseja {state.active.active ? 'desativar' : 'ativar'} o formulário <span style={{ fontWeight: 'bold' }}>{state.active.name}</span>?
                    digite <span style={{ fontWeight: 'bold' }}>{state.active.active ? 'desativar' : 'ativar'}</span> no campo abaixo.</>}
                open={true}
                handleClose={() => setState({ active: null })}
                handleSubmit={toggle}
            />}
            {state.delete && <ConfirmationDialogCaptcha
                captcha={'remover'}
                title='Remover formulário'
                description={<>Deseja remover o formulário <span style={{ fontWeight: 'bold' }}>{state.delete.name}</span>?
                    digite <span style={{ fontWeight: 'bold' }}>remover</span> no campo abaixo.</>}
                open={true}
                handleClose={() => setState({ delete: null })}
                handleSubmit={remove}
            />}
            {state.edit &&
                <EditWebForms
                    data={state.edit}
                    contactCustomFields={state.contactCustomFields}
                    contactEnterpriseCustomFields={state.contactEnterpriseCustomFields}
                    journeyCustomFields={state.journeyCustomFields}
                    journey={journey}
                    handleClose={() => {
                        setState({ edit: null });
                        initialize()
                    }}
                />
            }
            <Dialog
                open={true}
                onClose={() => { }}
                aria-labelledby="alert-dialog-slide-title"
                aria-describedby="alert-dialog-slide-description"
                fullWidth
                maxWidth="sm"
                sx={{ height: '100%' }}
                PaperProps={{ sx: { height: '100%' } }}
            >
                <DialogTitle id="alert-dialog-slide-title" sx={{ display: "flex", justifyContent: "space-between" }}>
                    Formulários
                    <Box>
                        {userGroups.isSupervisor &&
                            <Button onClick={() => window.open("https://instant.getoutline.com/s/e08796ee-d3a6-49f3-b557-7a2b6bd636e0", "_blank")}>FAQ</Button>
                        }
                        <Button sx={{ 'minWidth': '2rem', 'padding': '0' }} onClick={handleClose} ><CloseIcon /></Button>
                    </Box>
                </DialogTitle >
                <Divider />
                <DialogContent>
                    <Box className="table-screen" height='100%'>
                        <ThemeProvider theme={theme}>
                            <DataGridPro
                                columns={buildColumns(state, setState)}
                                rows={state.rows}
                                components={{
                                    Toolbar: CustomToolbar
                                }}
                                disableSelectionOnClick
                                density={'compact'}
                            />
                        </ThemeProvider>
                    </Box>
                </DialogContent>

            </Dialog>
        </>
    )
}

export default WebForms